import { z } from 'zod';

import objectPassthrough from '../../objectPassthrough';
import { newUuid, UUID } from '../../uuid';
import { ProtectedValue, Unprotect, UnprotectedValue } from '../ProtectedValue';

interface CustomFieldAutofillSettings {
  enabled: boolean;
  fieldIdOrName: string;
}

const CustomFieldAutofillSettings = objectPassthrough({
  enabled: z.boolean(),
  fieldIdOrName: z.string(),
});

interface ProtectedCustomField {
  readonly id: UUID;
  readonly name: string;
  readonly protected: true;
  readonly value: ProtectedValue;
  readonly multiline?: boolean;
  // if set, use it for autofill purposes instead of name
  readonly autofill?: CustomFieldAutofillSettings;
}

const ProtectedCustomField = objectPassthrough({
  // Id should be set most of the time, since it was set for all CustomFields during the automerge
  // migration. However at that time not all importers set the id correctly, so performed in
  // heymerge accounts may have re-introduced id-less CustomFields. In this case we generate an id
  // during parsing.
  id: z.uuid().default(newUuid),
  name: z.string(),
  protected: z.literal(true),
  value: ProtectedValue,
  multiline: z.boolean().optional(),
  autofill: CustomFieldAutofillSettings.optional(),
});

type UnprotectedProtectedCustomField = Unprotect<ProtectedCustomField, UnprotectedValue>;

const UnprotectedProtectedCustomField = objectPassthrough({
  // see ProtectedCustomField.id
  id: z.uuid().default(newUuid),
  name: z.string(),
  protected: z.literal(true),
  value: UnprotectedValue,
  multiline: z.boolean().optional(),
  autofill: CustomFieldAutofillSettings.optional(),
});

interface PlainCustomField {
  readonly id: UUID;
  readonly name: string;
  readonly protected: false;
  readonly value: string;
  readonly multiline?: boolean;
  // if set, use it for autofill purposes instead of name
  autofill?: CustomFieldAutofillSettings;
}

const PlainCustomField = objectPassthrough({
  // see ProtectedCustomField.id
  id: z.uuid().default(newUuid),
  name: z.string(),
  protected: z.literal(false),
  value: z.string(),
  multiline: z.boolean().optional(),
  autofill: CustomFieldAutofillSettings.optional(),
});

type CustomField = ProtectedCustomField | PlainCustomField;
const CustomField = z.union([ProtectedCustomField, PlainCustomField]);

type UnprotectedCustomField = UnprotectedProtectedCustomField | PlainCustomField;
const UnprotectedCustomField = z.union([UnprotectedProtectedCustomField, PlainCustomField]);

export { PlainCustomField, ProtectedCustomField, CustomField, UnprotectedCustomField };
